import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import Checkbox from "@material-ui/core/Checkbox";
import Check from "@material-ui/icons/Check";
import NotificationsActive from "@material-ui/icons/NotificationsActive";
import ArrowBack from "@material-ui/icons/ArrowBack";
import { Delete, Edit } from '@material-ui/icons';
import regularFormsStyle from "assets/jss/material-dashboard-pro-react/views/regularFormsStyle";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardIcon from "components/Card/CardIcon.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomSelect from "components/CustomSelect/CustomSelect";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import { TransReactTable } from 'components/CustomReactTable/TransReactTable';
import LoadingModal from 'components/Loading/Loading';
import Toast from 'views/Toast/Toast';
import { CallApi } from 'utils/Request';
import { useTranslation } from 'react-i18next';
import { verifyEmail } from 'utils/Validate';

const useStyles = makeStyles((theme) => ({
    ...regularFormsStyle,
    answerBox: {
        border: '1px solid #e2e8e4', borderRadius: 10, padding: '6px 10px', marginTop: 6,
        maxHeight: 220, overflowY: 'auto', background: '#fafdfb'
    },
    descriptionTextArea: { width: "100%", height: 90 },
    scopeBtn: {
        border: '1px solid #cfd8d3', background: '#fff', color: '#5d6d66', fontFamily: 'inherit',
        fontSize: 13.5, fontWeight: 600, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', marginRight: 8
    },
    scopeBtnActive: { border: '1px solid #2f9e44', background: '#2f9e44', color: '#fff' },
}));

// Answer options of a rating (radio) question, incl. star-type questions whose values are
// implicit (1..N) with no stored options. Non-radio questions have no numeric value → empty.
function optionsOfQuestion(q) {
    const qq = (q && q.question) || {};
    if (qq.type !== 'radio') return [];
    if (qq.direction === '5star' || qq.direction === '10star') {
        const n = qq.direction === '10star' ? 10 : 5;
        const arr = [];
        for (let v = 1; v <= n; v++) arr.push({ value: v, label: `${v} ★` });
        return arr;
    }
    return (qq.options || []).map(o => ({ value: o.value, label: o.label }));
}

export default function SurveyQuestionAlerts(props) {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const surveyId = props.match.params.survey_id;

    const [isLoading, setIsLoading] = useState(false);
    const [surveyTitle, setSurveyTitle] = useState('');
    const [sections, setSections] = useState([]);       // [{ sectionTitle, questions:[...] }]
    const [alerts, setAlerts] = useState([]);

    // form state
    const [editingId, setEditingId] = useState(null);   // null = adding; id = editing that rule
    const [scope, setScope] = useState('question');     // 'question' | 'section'
    const [sectionIndex, setSectionIndex] = useState(null);
    const [questionId, setQuestionId] = useState(null);
    const [selectedValues, setSelectedValues] = useState([]); // raw option values
    const [email, setEmail] = useState('');
    // 'values' names the answers that fire the rule; 'below' fires when the score falls under a
    // threshold — for a section that is the average of its answered rating questions.
    const [triggerMode, setTriggerMode] = useState('values');
    const [threshold, setThreshold] = useState('3.8');
    const [department, setDepartment] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => { loadSurvey(); loadAlerts(); /* eslint-disable-next-line */ }, []);

    const loadSurvey = () => {
        setIsLoading(true);
        CallApi({ url: `survey/get?id=${surveyId}`, method: 'get' }).then((res) => {
            if (res.success && res.data) {
                const d = res.data;
                const main = (d.main || []).find(m => m.language === i18n.language) || (d.main || [])[0];
                setSurveyTitle(main ? main.title : ('#' + surveyId));
                const variants = Array.isArray(d.questions) ? d.questions : [];
                const variant = variants.find(v => v && v.language === i18n.language) || variants[0];
                setSections((variant && variant.sections) ? variant.sections : []);
            } else {
                Toast(400, res.error || 'Error loading survey');
            }
        }).catch(() => Toast(500)).finally(() => setIsLoading(false));
    };

    const loadAlerts = () => {
        CallApi({ url: `rate_message/question_alert/list?survey_id=${surveyId}`, method: 'get' }).then((res) => {
            if (res.success) setAlerts(res.data || []);
        }).catch(() => { /* list is non-critical */ });
    };

    // Current section + the rating questions inside it
    const currentSection = (sectionIndex != null) ? sections[sectionIndex] : null;
    const ratingQuestions = currentSection
        ? (currentSection.questions || []).filter(q => q && q.question && q.question.type === 'radio')
        : [];
    const selectedQuestion = (scope === 'question' && questionId)
        ? ratingQuestions.find(q => q.id === questionId) : null;

    // The answer options offered for "which answers trigger the alert":
    // question scope → that question's options; section scope → the union across the section's
    // rating questions (deduped by value), so one set covers a same-scale section.
    const triggerOptions = (() => {
        if (scope === 'question') return selectedQuestion ? optionsOfQuestion(selectedQuestion) : [];
        const seen = {}, out = [];
        ratingQuestions.forEach(q => optionsOfQuestion(q).forEach(o => {
            const k = String(o.value);
            if (!(k in seen)) { seen[k] = true; out.push(o); }
        }));
        return out;
    })();

    const toggleValue = (v) => {
        const k = String(v);
        setSelectedValues(prev => prev.map(String).indexOf(k) >= 0
            ? prev.filter(x => String(x) !== k)
            : prev.concat([v]));
    };

    // Reset only the answer/email/department/message (keeps the chosen section/question so you
    // can quickly add several rules for the same target).
    const resetForm = () => {
        setSelectedValues([]); setEmail(''); setDepartment(''); setMessage('');
    };
    // Full reset back to a clean "add" state.
    const fullReset = () => {
        setEditingId(null); setScope('question'); setSectionIndex(null); setQuestionId(null);
        setSelectedValues([]); setEmail(''); setDepartment(''); setMessage('');
    };

    // Load an existing rule into the form for editing.
    const startEdit = (a) => {
        setEditingId(a.id);
        setScope(a.scope === 'section' ? 'section' : 'question');
        setSectionIndex(a.section_index != null ? a.section_index : null);
        setQuestionId(a.scope === 'section' ? null : (a.question_id || null));
        setSelectedValues(Array.isArray(a.trigger_values) ? a.trigger_values : []);
        setTriggerMode(a.trigger_mode === 'below' ? 'below' : 'values');
        setThreshold(a.threshold != null ? String(a.threshold) : '3.8');
        setEmail(a.email || '');
        setDepartment(a.department || '');
        setMessage(a.message || '');
        try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { /* ignore */ }
    };

    const onSave = () => {
        if (sectionIndex == null) { Toast(400, t('Please choose a section')); return; }
        if (scope === 'question' && !questionId) { Toast(400, t('Please choose a question')); return; }
        if (triggerMode === 'values' && selectedValues.length === 0) {
            Toast(400, t('Select at least one answer that triggers the alert')); return;
        }
        if (triggerMode === 'below' && !(parseFloat(threshold) > 0)) {
            Toast(400, t('Enter the score below which the alert fires')); return;
        }
        // A department is usually several people, so the field takes a comma-separated list and
        // every address in it has to be valid — one typo would otherwise fail silently at send time.
        const recipients = email.split(',').map(e => e.trim()).filter(Boolean);
        if (!recipients.length) { Toast(400, t('Invalid email format')); return; }
        const bad = recipients.filter(e => !verifyEmail(e));
        if (bad.length) { Toast(400, t('Invalid email address') + ': ' + bad.join(', ')); return; }

        const labelsByValue = {};
        triggerOptions.forEach(o => { labelsByValue[String(o.value)] = o.label; });
        const payload = {
            survey_id: surveyId,
            scope,
            section_index: sectionIndex,
            section_title: currentSection ? currentSection.sectionTitle : '',
            question_id: scope === 'question' ? questionId : null,
            question_title: scope === 'question' && selectedQuestion ? selectedQuestion.questionTitle : null,
            trigger_values: triggerMode === 'values' ? selectedValues : null,
            trigger_labels: triggerMode === 'values'
                ? selectedValues.map(v => labelsByValue[String(v)] || String(v)) : null,
            trigger_mode: triggerMode,
            threshold: triggerMode === 'below' ? parseFloat(threshold) : null,
            email: recipients.join(', '),
            department: department.trim(),
            message: message.trim(),
        };
        const url = editingId
            ? `rate_message/question_alert/update/${editingId}`
            : 'rate_message/question_alert/add';
        setIsLoading(true);
        CallApi({ url, method: 'post', data: payload }).then((res) => {
            if (res.success) { if (editingId) fullReset(); else resetForm(); loadAlerts(); Toast(200, t('Saved')); }
            else Toast(400, res.error);
        }).catch(() => Toast(500)).finally(() => setIsLoading(false));
    };

    const onToggle = (id) => {
        CallApi({ url: `rate_message/question_alert/toggle/${id}`, method: 'post' }).then((res) => {
            if (res.success) loadAlerts(); else Toast(400, res.error);
        }).catch(() => Toast(500));
    };
    const onDelete = (id) => {
        if (!window.confirm(t('Are you sure?'))) return;
        setIsLoading(true);
        CallApi({ url: `rate_message/question_alert/delete/${id}`, method: 'delete' }).then((res) => {
            if (res.success) loadAlerts(); else Toast(400, res.error);
        }).catch(() => Toast(500)).finally(() => setIsLoading(false));
    };

    const rows = alerts.map(a => ({
        ...a,
        scope_label: a.scope === 'section' ? t('Whole section') : t('Question'),
        target: a.scope === 'section' ? (a.section_title || ('#' + a.section_index)) : (a.question_title || a.question_id),
        triggers_label: a.trigger_mode === 'below'
            ? t('below {n}').replace('{n}', a.threshold)
            : (Array.isArray(a.trigger_labels) && a.trigger_labels.length
                ? a.trigger_labels.join(', ')
                : (Array.isArray(a.trigger_values) ? a.trigger_values.join(', ') : '')),
        active_cell: (
            <Button color={a.active ? 'success' : 'white'} size='sm' onClick={() => onToggle(a.id)}>
                {a.active ? t('Active') : t('Inactive')}
            </Button>
        ),
        actions: (
            <div style={{ whiteSpace: 'nowrap' }}>
                <Button justIcon round color='info' size='sm' onClick={() => startEdit(a)}><Edit style={{ fontSize: 16 }} /></Button>
                <Button justIcon round color='danger' size='sm' style={{ marginLeft: 6 }} onClick={() => onDelete(a.id)}><Delete style={{ fontSize: 16 }} /></Button>
            </div>
        ),
    }));

    return (
        <div>
            <GridContainer>
                <GridItem xs={12}>
                    <Button color="white" size="sm" onClick={() => props.history.push('/survey/survey')}>
                        <ArrowBack /> {t('Back to surveys')}
                    </Button>

                    <Card>
                        <CardHeader color="success" icon>
                            <CardIcon color="success"><NotificationsActive /></CardIcon>
                            <h4 className={classes.cardIconTitle}>
                                {editingId ? t('Edit alert') : t('Question alerts')} — {surveyTitle}
                            </h4>
                        </CardHeader>
                        <CardBody>
                            <p style={{ color: '#5d6d66', fontSize: 13.5, marginTop: -6 }}>
                                {t('Notify a specific person when a single question — or a whole section — gets a bad answer, independently of the overall survey score.')}
                            </p>

                            {/* Scope */}
                            <div style={{ margin: '8px 0 14px' }}>
                                <button type="button"
                                    className={scope === 'question' ? `${classes.scopeBtn} ${classes.scopeBtnActive}` : classes.scopeBtn}
                                    onClick={() => { setScope('question'); setSelectedValues([]); }}>
                                    {t('A specific question')}
                                </button>
                                <button type="button"
                                    className={scope === 'section' ? `${classes.scopeBtn} ${classes.scopeBtnActive}` : classes.scopeBtn}
                                    onClick={() => { setScope('section'); setQuestionId(null); setSelectedValues([]); }}>
                                    {t('A whole section')}
                                </button>
                            </div>

                            <GridContainer>
                                {/* Section */}
                                <GridItem xs={12} md={scope === 'question' ? 6 : 12}>
                                    <label style={{ fontSize: 12, color: '#8a988f', fontWeight: 600 }}>{t('Section')}</label>
                                    <CustomSelect
                                        placeholder={t('Section')}
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                        value={sectionIndex != null && currentSection ? { label: currentSection.sectionTitle, value: sectionIndex } : null}
                                        onChange={(opt) => { setSectionIndex(opt ? opt.value : null); setQuestionId(null); setSelectedValues([]); }}
                                        options={sections.map((s, i) => ({ label: s.sectionTitle, value: i }))}
                                    />
                                </GridItem>

                                {/* Question (question scope only) */}
                                {scope === 'question' &&
                                    <GridItem xs={12} md={6}>
                                        <label style={{ fontSize: 12, color: '#8a988f', fontWeight: 600 }}>{t('Question')}</label>
                                        <CustomSelect
                                            placeholder={t('Question')}
                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                            value={selectedQuestion ? { label: selectedQuestion.questionTitle, value: questionId } : null}
                                            onChange={(opt) => { setQuestionId(opt ? opt.value : null); setSelectedValues([]); }}
                                            options={ratingQuestions.map(q => ({ label: q.questionTitle, value: q.id }))}
                                        />
                                    </GridItem>
                                }
                            </GridContainer>

                            {/* What makes the rule fire: named answers, or a score threshold. */}
                            <div style={{ marginTop: 12 }}>
                                <label style={{ fontSize: 12, color: '#8a988f', fontWeight: 600 }}>
                                    {t('Fire the alert when:')}
                                </label>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '6px 0' }}>
                                    <button type="button" onClick={() => setTriggerMode('values')}
                                        className={triggerMode === 'values' ? `${classes.scopeBtn} ${classes.scopeBtnActive}` : classes.scopeBtn}>
                                        {t('the answer is one of…')}
                                    </button>
                                    <button type="button" onClick={() => setTriggerMode('below')}
                                        className={triggerMode === 'below' ? `${classes.scopeBtn} ${classes.scopeBtnActive}` : classes.scopeBtn}>
                                        {t('the score is below…')}
                                    </button>
                                </div>
                                {triggerMode === 'below' &&
                                    <div>
                                        <CustomInput labelText={t('Score below which the alert fires')} id="qa_threshold"
                                            formControlProps={{ onChange: e => setThreshold(e.target.value) }}
                                            inputProps={{ type: 'number', step: '0.1', value: threshold }} />
                                        <div style={{ fontSize: 12, color: '#8a988f', marginTop: 4 }}>
                                            {scope === 'section'
                                                ? t('Uses the average of the rating questions the guest answered in this section.')
                                                : t('Uses the answer given to this question.')}
                                        </div>
                                    </div>}
                            </div>

                            {/* Trigger answers */}
                            {triggerMode === 'values' && triggerOptions.length > 0 &&
                                <div style={{ marginTop: 10 }}>
                                    <label style={{ fontSize: 12, color: '#8a988f', fontWeight: 600 }}>
                                        {t('Notify when the answer is one of:')}
                                    </label>
                                    <div className={classes.answerBox}>
                                        {triggerOptions.map((o) => {
                                            const checked = selectedValues.map(String).indexOf(String(o.value)) >= 0;
                                            return (
                                                <div key={String(o.value)} style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Checkbox checked={checked} onClick={() => toggleValue(o.value)}
                                                        checkedIcon={<Check className={classes.checkedIcon} />}
                                                        icon={<Check className={classes.uncheckedIcon} />}
                                                        classes={{ checked: classes.checked, root: classes.checkRoot }} />
                                                    <span style={{ fontSize: 14 }}>{o.label} <span style={{ color: '#8a988f' }}>({String(o.value)})</span></span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {scope === 'section' &&
                                        <div style={{ fontSize: 12, color: '#8a988f', marginTop: 4 }}>
                                            {t('The alert fires if ANY rating question in this section gets one of the selected answers.')}
                                        </div>}
                                </div>
                            }

                            <GridContainer style={{ marginTop: 6 }}>
                                <GridItem xs={12} md={6}>
                                    <CustomInput labelText={t('Recipient email(s)')} id="qa_email"
                                        formControlProps={{ fullWidth: true, onChange: e => setEmail(e.target.value) }}
                                        inputProps={{ value: email }} />
                                    <div style={{ fontSize: 12, color: '#8a988f', marginTop: -8, marginBottom: 8 }}>
                                        {t('Several addresses separated by commas — the whole department gets one email.')}
                                    </div>
                                </GridItem>
                                <GridItem xs={12} md={6}>
                                    <CustomInput labelText={t('Department / label (optional)')} id="qa_dep"
                                        formControlProps={{ fullWidth: true, onChange: e => setDepartment(e.target.value) }}
                                        inputProps={{ value: department }} />
                                </GridItem>
                                <GridItem xs={12}>
                                    <textarea className={classes.descriptionTextArea}
                                        placeholder={t('Message (optional)')}
                                        value={message}
                                        onChange={e => setMessage(e.target.value)} />
                                </GridItem>
                            </GridContainer>

                            <Button color='success' onClick={onSave}>{editingId ? t('Update') : t('Save')}</Button>
                            {editingId &&
                                <Button color='white' onClick={fullReset} style={{ marginLeft: 8 }}>{t('Cancel edit')}</Button>}
                        </CardBody>
                    </Card>

                    <Card>
                        <CardHeader color="success" icon>
                            <CardIcon color="success"><NotificationsActive /></CardIcon>
                            <h4 className={classes.cardIconTitle}>{t('Configured alerts')}</h4>
                        </CardHeader>
                        <CardBody>
                            <TransReactTable
                                data={rows}
                                columns={[
                                    { Header: t('Scope'), accessor: 'scope_label', width: 110 },
                                    { Header: t('Section / Question'), accessor: 'target' },
                                    { Header: t('Triggers on'), accessor: 'triggers_label' },
                                    { Header: t('Email'), accessor: 'email' },
                                    { Header: t('Department'), accessor: 'department' },
                                    { Header: t('Status'), accessor: 'active_cell', sortable: false, filterable: false, width: 110 },
                                    { Header: 'Actions', accessor: 'actions', sortable: false, filterable: false, width: 120 },
                                ]}
                                defaultPageSize={20}
                                className="-striped -highlight"
                            />
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>
            {isLoading && <LoadingModal />}
        </div>
    );
}
